import { useDbStore } from './dbStore';
import { defineStore } from "pinia";
import { api, fetchAllData } from "../services/api.js";
import { isNative, getMobileRedirectUri, getPlatform, getOrCreateDeviceId } from "../services/platform.js";

// ── Helpers persistenza notifiche ──────────────────────────────────────────────
const NOTIF_STORAGE_KEY = 'push_notifications'
const NOTIF_MAX = 50
let pushRegistrationPromise = null

function _loadNotificationsFromStorage() {
  try {
    const raw = localStorage.getItem(NOTIF_STORAGE_KEY)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

function _saveNotificationsToStorage(notifications) {
  try {
    localStorage.setItem(NOTIF_STORAGE_KEY, JSON.stringify(notifications.slice(0, NOTIF_MAX)))
  } catch { /* quota exceeded o altro — silent */ }
}

export const useAppStore = defineStore("auth", {
    // ...
  state: () => ({

    accessToken: localStorage.getItem("access_token") || null,
    refreshToken: localStorage.getItem("refresh_token") || null,
    // expiresAt: localStorage.getItem("expires_at") || null,
    user: JSON.parse(localStorage.getItem("user") || "null"),
    loadingAuth: false,
    authError: null,
    // true quando serverConfig è stata caricata dalla cache locale (server non raggiungibile)
    isOffline: false,

    // Ripristina serverConfig: prima dal sessionStorage (tab corrente), poi da localStorage (persistente)
    serverConfig: (() => {
      try {
        const raw = sessionStorage.getItem("serverConfig") || localStorage.getItem("poderi2_server_config");
        return JSON.parse(raw || "null");
      } catch (e) {
        console.warn("serverConfig malformato, ignorato.", e);
        return null;
      }
    })(),

    //Stati usati per le chiamate API
    apiState: "init", // init, loading, loaded, error
    apiResult: {},

    //Eventuale titolo della pagina
    title: null,

    sections: [], //sezioni visibili per l'utente

    // Push notifications — persistite in localStorage (cap 50 voci)
    pushToken: localStorage.getItem('push_token') || null,
    notifications: _loadNotificationsFromStorage(),
    unreadCount: _loadNotificationsFromStorage().filter(n => !n.read).length,
  }),

  getters: {
    /**
     * Restituisce la configurazione Dexie generata dalla metadata del server
     */
    dexieConfig(state) {

      const config = {};
      const tables = state.serverConfig?.metadata?.tables;
      if (tables) {
        for (const tableMeta of Object.values(tables)) {
          config[tableMeta.table_name] = tableMeta.primary_key;
        }
      }

      // Tabelle chat2 — gestione separata (non passano dal sync standard)
      config['chat2_room']         = 'id_room, room_type, broadcast_recipient_uid, broadcast_sender_uid, id_farm_center';
      config['chat2_message']      = 'id_message, id_room, created_at, _to_synch, client_id';
      config['chat2_message_read'] = '[id_message+uid], uid, id_message';

      return config;
    },
    

    isLoading(state) {
      return state.loadingAuth;
    },

    hasServerConfig(state) {
      return state.serverConfig !== null;
    },

    getAuthConfig(state) {
      return state.serverConfig?.auth?.oauth2 || null;
    },

    isAuthenticated(state) {
      if (state.user && (state.user.uid > 0 || state.user?.superuser?.sid > 0)) {
        return true;
      }
      return false;
    },

    isAdmin(state) {
      let roles = state.user?.roles || [];
      return roles.includes("admin");
    },

    getUserName(state) {
      let name = "";
      if (state.user?.superuser) {
        name = state.user.superuser.realname || "";
        let surname = state.user.superuser.realsurname || "";
        name = name + " " + surname;
      }

      if (state.user?.profiles && state.user.profiles.length > 1) {
        name = name + " (" + state.user.name + ")";
      }

      return name;
    },
  },

  actions: {
    /**
     * Sincronizza il db locale con i dati dal backend
     * @param {string} profileId - id del profilo/azienda attivo
     */
    async syncLocalDb(profileId) {
      const dbStore = useDbStore();
      const config = this.dexieConfig;
      await dbStore.syncDbFromBackend(profileId, config, async () => {
        const res = await fetchAllData();
        return res;
      });
    },

    // Controlla se l'utente ha nuove farm_center rispetto a quelle in locale.
    // Chiamato al ritorno in foreground (appStateChange / visibilitychange).
    // Fa il sync completo solo se ci sono nuove farm — altrimenti è no-op.
    async checkForNewFarmCenters() {
      if (!this.isAuthenticated) return
      const dbStore = useDbStore()
      if (dbStore.isSyncingFull) {
        console.log('[checkForNewFarmCenters] full sync in corso, skip')
        return
      }
      try {
        const res = await api.get('/api/v2/poderi2/farm_center')
        if (!res.ok) return
        const remoteFcIds = new Set((res.data || []).map(fc => fc.id_farm_center))
        const localResult = await dbStore.select('farm_center')
        const localFcIds = new Set((localResult?.data || []).map(fc => fc.id_farm_center))
        const hasNew = [...remoteFcIds].some(id => !localFcIds.has(id))
        if (hasNew) {
          const profileId = this.user?.uid
          if (profileId) await this.syncLocalDb(profileId)
        }
      } catch (e) {
        console.warn('[checkForNewFarmCenters]', e)
      }
    },
    async fetchServerConfig() {
      this.loadingAuth = true;
      this.authError = null;

      try {
        const _gd = getOrCreateDeviceId()
        const res = await api.get("/api/v2/config", _gd ? { params: { guid_device: _gd } } : undefined)
        if (!res.ok) {
          throw new Error(res.message || "Errore caricamento config");
        }
        const config = res.data;
        this.serverConfig = config;
        this.isOffline = false;

        // Salva in sessionStorage (tab corrente) e localStorage (cache persistente per mobile)
        sessionStorage.setItem("serverConfig", JSON.stringify(config));
        localStorage.setItem("poderi2_server_config", JSON.stringify(config));

        // Aggiorna i dati utente se restituiti
        if (config.user) {
          this.user = config.user;
          localStorage.setItem("user", JSON.stringify(config.user));
        }

        console.log("Server config loaded:", config);
        return config;
      } catch (error) {
        console.error("Error fetching server config:", error);

        // Fallback 1: cache localStorage (fondamentale su mobile dove sessionStorage
        // si svuota ad ogni restart dell'app)
        const cachedRaw = localStorage.getItem("poderi2_server_config");
        if (cachedRaw) {
          try {
            const cached = JSON.parse(cachedRaw);
            this.serverConfig = cached;
            this.isOffline = true;
            console.warn("Server non raggiungibile — uso serverConfig da cache locale");
            // Non rilanciare: l'app continua con i dati in cache
            return cached;
          } catch (parseErr) {
            console.error("Cache serverConfig corrotta:", parseErr);
            localStorage.removeItem("poderi2_server_config");
          }
        }

        // Fallback 2: nessuna cache disponibile → errore definitivo
        this.authError = error.message;
        throw error;
      } finally {
        this.loadingAuth = false;
      }
    },

    login() {
      //la redirectURI la generiamo dal serevr
      let self = this;
      let oauth = this.serverConfig.auth.oauth2;
      var redirect_uri = oauth.redirect_uri;

      // Su nativo (iOS/Android) sostituiamo il redirect_uri con il custom URL scheme
      // es. "poderi2://auth/callback" — iniettato in index.html da mobile/build.sh
      if (isNative()) {
        redirect_uri = getMobileRedirectUri()
      }
      var scope = "";
      if (oauth.scopes) {
        scope = "&scope=" + oauth.scopes.join(" ");
      }
      let url = "";
      if (oauth.authorization_endpoint) {
        url = oauth.authorization_endpoint;
      } else {
        url = oauth["auth_url"];
      }
      let oauth2_authorize =
        url +
        "?response_type=code" +
        scope +
        "&redirect_uri=" +
        redirect_uri +
        "&client_id=" +
        oauth.client_id;
      try {
        const redirectUrl = window.location.href;
        localStorage.setItem("post_login_redirect", redirectUrl);
      } catch (e) {
        console.error(
          "Error saving post login redirect URL to localStorage:",
          e
        );
      }

      if (isNative()) {
        // Su nativo apriamo Keycloak in SFSafariViewController (iOS) / Chrome Custom Tab (Android).
        // Il redirect poderi2://auth/callback viene intercettato dal sistema e consegnato
        // all'app tramite l'evento appUrlOpen (listener già attivo in App.vue).
        import(/* @vite-ignore */ '@capacitor/browser').then(({ Browser }) => {
          Browser.open({ url: oauth2_authorize })
        })
      } else {
        window.location = oauth2_authorize
      }
    },

    /**
     * Registra il token FCM del device sul backend — fire-and-forget, non blocca l'avvio.
     * Viene chiamato dopo fetchServerConfig() / syncDbAfterAuth().
     */
    async registerPushToken() {
      if (!isNative()) return
      if (pushRegistrationPromise) return pushRegistrationPromise

      pushRegistrationPromise = (async () => {
        try {
          const { requestAndGetToken, getOrCreateDeviceId } = await import('../composables/useNotifications.js')
          const token = await requestAndGetToken()
          if (!token) return null
          this.pushToken = token
          localStorage.setItem('push_token', token)
          const guid_device = getOrCreateDeviceId()
          // Se il server non risponde, il token verrà ri-inviato al prossimo avvio.
          await api.post('/api/v2/notifications/register', {
            push_token: token,
            guid_device,
            platform: getPlatform(), // 'ios' | 'android'
          })
          return token
        } catch (e) {
          console.error('[Push] registerPushToken:', e)
          return null
        } finally {
          pushRegistrationPromise = null
        }
      })()

      return pushRegistrationPromise
    },

    /**
     * Aggiunge una notifica alla lista in-memory (dedup per ID).
     * @param {{ id, title, body, data }} notification
     */
    addNotification(notification) {
      const exists = this.notifications.some(n => n.id === notification.id)
      if (!exists) {
        this.notifications.unshift({ ...notification, read: false, receivedAt: Date.now() })
        if (this.notifications.length > NOTIF_MAX) {
          this.notifications = this.notifications.slice(0, NOTIF_MAX)
        }
        this.unreadCount++
        _saveNotificationsToStorage(this.notifications)
      }
    },

    /** Segna tutte le notifiche come lette */
    markAllRead() {
      this.notifications.forEach(n => { n.read = true })
      this.unreadCount = 0
      _saveNotificationsToStorage(this.notifications)
    },

    /**
     * Sincronizza il db locale dopo login o cambio profilo
     * @param {string} profileId
     */
    async syncDbAfterAuth(profileId) {
      // Persiste il profilo attivo in localStorage: viene poi inviato come
      // header X-Selected-Profile su ogni chiamata API. Deve sopravvivere
      // al riavvio dell'app (fondamentale su Capacitor).
      if (profileId) {
        localStorage.setItem('selected_profile', profileId);
      }

      // Usa la computed dexieConfig
      await this.syncLocalDb(profileId);

      // Connetti allo stream SSE per ricevere aggiornamenti da altri utenti
      const dbStore = useDbStore();
      dbStore.connectStream();

      // Avvia i watcher per resync automatico (online/visibility/timer)
      const config = this.dexieConfig;
      dbStore.startSyncWatchers({
        config,
        fetchDataFn: async () => await fetchAllData()
      });

      // Registra il token FCM — fire-and-forget, non blocca
      this.registerPushToken()
    },

    hasRole(role) {
      let roles = this.user?.roles || [];
      return roles.includes(role);
    },


    hasFeatureFlag(flag) {


      let flags = this.user?.feature_flag || [];
      for (let f of flags) {
        if (f.id_feature === flag && f.is_enabled) {
          return true;
        }
      }
      return false;
    },

    //solve the code oauth2 and get the token and userinfo
    async resolveCodeOAuth2() {


      
      // Con hash routing (createWebHashHistory) l'URL dopo il redirect è:
      //   http://host/#/auth/callback?code=xxx
      // window.location.search è vuoto perché i parametri sono dentro il
      // fragment hash. Estraiamo i parametri dalla parte dopo "?" nell'hash.
      const hashParts = window.location.hash.split("?");
      const queryString = hashParts.length > 1 ? hashParts[1] : window.location.search.substring(1);
      const params = new URLSearchParams(queryString);
      const code = params.get("code");
      // const state = params.get('state');

      if (code) {
        const self = this;
        self.loadingAuth = true;

        // serverConfig è già caricata da App.vue (fetchServerConfig viene
        // chiamata prima che AuthCallback venga montato)
        const authConfig = this.getAuthConfig;
        if (!authConfig) {
          self.authError = "Configurazione OAuth2 non disponibile";
          self.loadingAuth = false;
          return;
        }

        console.log("Code ottenuto da oauth2: ", code);

          try {
            const _gd = getOrCreateDeviceId()
            const res = await api.get("/api/v2/config", {
              params: {
                code: code,
                oauth_to_be_use: "oauth2",
                // Su nativo bisogna usare il mobile redirect_uri (custom URL scheme)
                // perché Keycloak verifica che corrisponda a quello inviato in authorize
                redirect_uri: isNative() ? getMobileRedirectUri() : authConfig.redirect_uri,
                ...(_gd ? { guid_device: _gd } : {}),
              },
            });

            if (!res.ok) {
              throw new Error(res.message || "Errore ottenimento token");
            }

            const data = res.data;

            if (data?.user?.access_token) {
              const access_token = data.user.access_token;
              const refresh_token = data.user.refresh_token;
              const user_info = data.user;

              this.setTokens({
                access_token,
                refresh_token,
                expires_in: data.expires_in || user_info.expires_in || 3600,
                user: user_info,
              });

              // La risposta /config con code contiene anche metadata e tabelle.
              // Aggiorniamo serverConfig così dexieConfig avrà lo schema corretto
              // (la prima fetchServerConfig in App.vue era senza utente autenticato,
              // quindi metadata/tabelle erano vuote).
              this.serverConfig = data;
              sessionStorage.setItem("serverConfig", JSON.stringify(data));
              localStorage.setItem("poderi2_server_config", JSON.stringify(data));

              // Re-inizializza Dexie con lo schema completo (ora include le tabelle)
              const dbStore = useDbStore();
              await dbStore.init(this.dexieConfig);

              // Dopo il login, sincronizza il db locale
              const profileId = user_info?.uid;
              if (profileId) {
                await this.syncDbAfterAuth(profileId);
              }
            } else {
              console.error("Nessun access_token ricevuto: ", data);
              throw new Error("Token non ricevuto");
            }

            console.log("Token ottenuto: ", data);
          } catch (error) {
            console.error("Errore durante l'ottenimento del token: ", error);
            this.authError = error.message;
          } finally {
            // Rimuove i parametri query dall'URL mantenendo l'hash route.
            // Con hash routing l'URL è tipo: http://host/#/auth/callback?code=xxx
            // Vogliamo pulirlo a: http://host/#/auth/callback (senza ?code=...)
            const cleanHash = window.location.hash.split("?")[0];
            window.history.replaceState(
              {},
              document.title,
              window.location.pathname + cleanHash
            );

            self.loadingAuth = false;
          }
      }
    },

    /**
     * Imposta il token dopo il login
     */
    setTokens({ access_token, refresh_token, expires_in, user }) {
      // Se expires_in non è fornito, usa 1 ora come default
      const expiresAt = Date.now() + (expires_in || 3600) * 1000;

      this.accessToken = access_token;
      this.refreshToken = refresh_token;
      this.expiresAt = expiresAt;
      this.user = user || null;

      // Salva in localStorage: i token devono sopravvivere al riavvio dell'app
      // (essenziale su Capacitor). XSS risk è equivalente a sessionStorage per
      // access token short-lived; il refresh token è protetto dal sandbox Capacitor.
      localStorage.setItem("access_token", access_token);
      localStorage.setItem("refresh_token", refresh_token);
      localStorage.setItem("expires_at", expiresAt);
      localStorage.setItem("user", JSON.stringify(user || {}));
    },

    /**
     * Pulisce i dati di autenticazione
     */
    async logout() {
      let self = this;
      self.loadingAuth = true;

      // Disconnetti SSE stream
      const dbStore = useDbStore();
      dbStore.disconnectStream();

      // Rimuovi token e profilo PRIMA della chiamata al server
      // così la request non porta Authorization né X-Selected-Profile
      localStorage.removeItem("access_token");
      localStorage.removeItem("refresh_token");
      localStorage.removeItem("expires_at");
      localStorage.removeItem("user");
      localStorage.removeItem("selected_profile");
      sessionStorage.removeItem("selected_profile");

      this.accessToken = null;
      this.refreshToken = null;
      this.expiresAt = null;
      this.user = null;

      try {
        const _gd = getOrCreateDeviceId()
        const _logoutParams = { logout: 'true', ...(_gd ? { guid_device: _gd } : {}) }
        const logout_res = await api.get("/api/v2/config", { params: _logoutParams });
        console.log("Logout server response:", logout_res);
      } catch (e) {
        console.error("Errore durante logout server:", e);
      }

      self.loadingAuth = true;
      window.location.href = '/';
    },

    /**
     * Pulisce l'errore di autenticazione
     */
    clearAuthError() {
      this.authError = null;
    },

    /**
     * Cambia profilo utente
     */
    async switchProfile(profile) {
      this.loadingAuth = true;
      this.authError = null;
      
      try {
        // IMPORTANTE: Salva il profilo PRIMA di chiamare l'API
        // così l'header X-Selected-Profile sarà presente nelle chiamate successive
        localStorage.setItem('selected_profile', profile.uid);
        
        // Usa query param per la chiamata corrente (il backend lo legge in get_user_keycloak)
        const _gd = getOrCreateDeviceId()
        const res = await api.get('/api/v2/config', {
          params: { selected_profile: profile.uid, ...(_gd ? { guid_device: _gd } : {}) }
        });
        
        if (res.ok) {
          const data = res.data;
          if (data.user) {
            // Aggiorna l'utente nello store e localStorage
            this.user = data.user;
            localStorage.setItem("user", JSON.stringify(data.user));

            // Se il backend torna anche access_token (es. dopo refresh), aggiornalo
            if (data.user.access_token) {
              localStorage.setItem("access_token", data.user.access_token);
              this.accessToken = data.user.access_token;
            }
            if (data.user.refresh_token) {
              localStorage.setItem("refresh_token", data.user.refresh_token);
              this.refreshToken = data.user.refresh_token;
            }

            // Aggiorna serverConfig se restituita
            if (data.metadata) {
              this.serverConfig = data;
              sessionStorage.setItem("serverConfig", JSON.stringify(data));
              localStorage.setItem("poderi2_server_config", JSON.stringify(data));
            }

            // Sincronizza il db locale dopo cambio profilo
            await this.syncDbAfterAuth(profile.uid);
          }
        } else {
          throw new Error(res.message || 'Errore nel cambio profilo');
        }
      } catch (e) {
        console.error("Errore nel cambio profilo:", e);
        this.authError = e.message || "Errore nel cambio profilo";
        this.loadingAuth = false;
        return;
      }
      window.location.href = '/';
    },
    /**
     * Forza la risincronizzazione del db locale (manuale)
     */
    async resyncDb() {
      const profileId = this.user?.uid || null;
      if (!profileId) return;

      await this.syncLocalDb(profileId);
    },

    /**
     * Forza un full resync completo (usato da SyncMonitor)
     */
    async forceFullResync() {
      const dbStore = useDbStore();
      await dbStore.forceFullResync();
    },

    /**
     * apiStore Esegue una chiamata API autenticata automaticamente
     * aggiorna i dati dello store relativi alla chiamata (apiState, apiResult)
     */
    async apiStore(url, options = {}) {
      const self = this;
      self.apiState = "loading";
      try {
        const res = await api.get(url, options);
        if (!res.ok) {
          self.apiState = "error";
          self.apiResult = res;
          throw new Error(res.message || "Errore API");
        }
        self.apiState = "loaded";
        self.apiResult = res.data;
        return res.data;
      } catch (error) {
        console.error("Errore nella chiamata API autenticata:", error);
        self.apiState = "error";
        self.apiResult = { ok: false, message: error.message };
      }
    },

    async apiCall(url, options = {}) {
      //fa la chiamata API autenticata ma non cambia lo stato dello store
      try {
        const res = await api.get(url, options);
        if (!res.ok) {
          throw new Error(res.message || "Errore API");
        }
        return res.data;
      } catch (error) {
        return {
          ok: false,
          message: error.message,
        };
      }
    },
  },
});
